import { QuickAddComponent } from '@theme/quick-add';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Most product templates use the Sternify product section, which has no Horizon
 * [data-product-grid-content] grid, so the quick add dialog had nothing to show.
 * The dialog now shows a simplified copy of the section from the product's own template.
 */
const SECTION_SELECTOR = '[class*="st_product-info--"]';
const SECTION_MODE_CLASS = 'quick-add-modal__content--section';
const QUICK_ADD_SECTION_ID = 'quick-add-section';
const ADDED_EVENT = 'quick-add:added';
const CONTINUE_TEXT = 'Seguir comprando';
const DETAILS_TEXT = 'Ver detalles del producto';
const CART_OPEN_GUARD_MS = 2000;

// Direct children of the section's details column that stay in the dialog.
const KEPT_DETAILS = [
  '.st_pi-title',
  ':has(.st_pi-price-row)',
  ':has(.st_usp-box-wrapper)',
  '.st_pi-options',
  ':has(.guia-tallas)',
  ':has(.st_pi-form)',
].join(',');

/**
 * @param {Element} parent
 * @param {string} [productUrl] - Adds a link to the product page when present
 */
function appendDialogActions(parent, productUrl) {
  const actions = document.createElement('div');
  actions.className = 'quick-add-actions';

  const continueButton = document.createElement('button');
  continueButton.type = 'button';
  continueButton.className = 'quick-add-actions__continue';
  continueButton.dataset.quickAddClose = '';
  continueButton.textContent = CONTINUE_TEXT;
  actions.append(continueButton);

  if (productUrl) {
    const detailsLink = document.createElement('a');
    detailsLink.className = 'quick-add-actions__details';
    detailsLink.href = productUrl;
    detailsLink.textContent = DETAILS_TEXT;
    actions.append(detailsLink);
  }

  parent.append(actions);
}

/**
 * Adds a simplified copy of the Sternify section to the fetched page as the dialog's grid.
 * @param {Document} html
 * @param {string} productPageUrl
 */
function addSectionGrid(html, productPageUrl) {
  const section = html.querySelector(SECTION_SELECTOR)?.closest('.shopify-section');
  if (!section) return;

  // A renamed copy can't collide with the same section on a product page.
  const sectionId = section.id.replace('shopify-section-', '');
  const grid = html.createElement('div');
  grid.toggleAttribute('data-product-grid-content', true);
  grid.toggleAttribute('data-quick-add-section', true);
  grid.innerHTML = section.innerHTML.replaceAll(sectionId, QUICK_ADD_SECTION_ID);

  const details = grid.querySelector('.st_pi-details');
  for (const child of details ? [...details.children] : []) {
    if (!child.matches(KEPT_DETAILS)) child.remove();
  }

  const swiperSource = section.querySelector('script[src*="swiper" i]');
  if (swiperSource) grid.dataset.swiperSrc = swiperSource.getAttribute('src') ?? '';

  // Only the section's own script is needed. It runs once the dialog is open so its gallery can measure.
  for (const script of grid.querySelectorAll('script:not([type="application/json"])')) {
    if (/var SECTION_ID\s*=/.test(script.textContent ?? '')) {
      script.type = 'text/plain';
      script.toggleAttribute('data-quick-add-init', true);
    } else {
      script.remove();
    }
  }

  if (details) appendDialogActions(details, productPageUrl);

  html.body.append(grid);
}

/** @param {string} src */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.append(script);
  });
}

/**
 * Runs the section's script for the copy in the dialog. It stays out of the page URL, and
 * announces the add instead of revealing the cart.
 * @param {Element} content
 */
async function initSection(content) {
  const source = content.querySelector('script[data-quick-add-init]');
  if (!source) return;
  source.remove();

  if (!window.Swiper && content.dataset.swiperSrc) await loadScript(content.dataset.swiperSrc);

  const script = document.createElement('script');
  script.textContent = (source.textContent ?? '')
    .replace('function updateUrl(variant) {', 'function updateUrl(variant) { return;')
    .replace(
      'function stRevealCart(added) {',
      `function stRevealCart(added) { document.dispatchEvent(new CustomEvent('${ADDED_EVENT}')); return;`
    );
  document.head.append(script);
  script.remove();
}

const { fetchProductPage, updateQuickAddModal } = QuickAddComponent.prototype;

/** @param {string} productPageUrl */
QuickAddComponent.prototype.fetchProductPage = async function (productPageUrl) {
  const html = await fetchProductPage.call(this, productPageUrl);
  if (!html) return html;

  if (!html.querySelector('[data-product-grid-content]')) addSectionGrid(html, productPageUrl);

  // Send shoppers to the product page rather than opening an empty dialog.
  if (!html.querySelector('[data-product-grid-content]')) {
    window.location.href = productPageUrl;
    throw new Error('Quick add content not found');
  }

  return html;
};

/** @param {Element} productGrid */
QuickAddComponent.prototype.updateQuickAddModal = async function (productGrid) {
  const modalContent = document.getElementById('quick-add-modal-content');
  if (!productGrid || !modalContent) return;

  const isSection = productGrid.hasAttribute('data-quick-add-section');
  modalContent.classList.toggle(SECTION_MODE_CLASS, isSection);

  if (!isSection) {
    await updateQuickAddModal.call(this, productGrid);
    appendDialogActions(modalContent.querySelector('.buy-buttons-block') ?? modalContent);
    return;
  }

  modalContent.setAttribute('view-event-payload', '');
  modalContent.dataset.swiperSrc = productGrid.getAttribute('data-swiper-src') ?? '';
  modalContent.replaceChildren(...productGrid.childNodes);
};

const dialogComponent = document.getElementById('quick-add-dialog');
const QuickAddDialog = customElements.get('quick-add-dialog');
const { showDialog } = QuickAddDialog.prototype;

QuickAddDialog.prototype.showDialog = function () {
  showDialog.call(this);

  const content = this.querySelector(`.${SECTION_MODE_CLASS}`);
  if (content) initSection(content);
};

dialogComponent?.addEventListener(DialogCloseEvent.eventName, () => {
  // Stops any gallery video still playing.
  dialogComponent.querySelector(`.${SECTION_MODE_CLASS}`)?.replaceChildren();
});

// The size guide's own script looks it up by id, which could find another copy on product pages.
dialogComponent?.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  if (event.target.closest('[data-quick-add-close]')) {
    dialogComponent.closeDialog();
    return;
  }

  const guide = dialogComponent.querySelector('.modal-guia');
  if (!(guide instanceof HTMLElement)) return;

  if (event.target.closest('[data-open-guia]')) {
    guide.style.display = 'flex';
    event.stopPropagation();
  } else if (event.target.closest('[data-close-guia]') || event.target === guide) {
    guide.style.display = 'none';
    event.stopPropagation();
  }
});

// Sternify's cart interceptor takes over the add before the form submits, never emits the standard
// cart event the dialog closes on, and opens its drawer afterwards. Its own event also fires for other
// cart changes, so only react to it after a click on the dialog's add button, captured on window to
// run ahead of the interceptor.
let isAddPending = false;
let cartOpenGuardUntil = 0;
let isCartOpenGuardBound = false;

/** Adding from the dialog only closes it: the product goes to the cart without showing the drawer. */
function closeAfterAdd() {
  isAddPending = false;
  cartOpenGuardUntil = Date.now() + CART_OPEN_GUARD_MS;

  const cart = window.SternifyCart;
  if (cart?.on && !isCartOpenGuardBound) {
    isCartOpenGuardBound = true;
    cart.on('open', () => {
      if (Date.now() > cartOpenGuardUntil) return;
      cartOpenGuardUntil = 0;
      cart.close();
    });
  }

  dialogComponent?.closeDialog();
  window.refreshCartCount?.();
}

window.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    const addButton = event.target.closest(
      'quick-add-dialog .add-to-cart-button, quick-add-dialog [data-st-atc]'
    );
    if (addButton && !addButton.disabled) isAddPending = true;
  },
  true
);

document.addEventListener('sternify:cart:updated', () => {
  if (isAddPending) closeAfterAdd();
});

document.addEventListener(ADDED_EVENT, closeAfterAdd);
